import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaUser, FaLock, FaShopify, FaExclamationCircle, FaCheckCircle, FaTicketAlt } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import './ShopifyRegister.css';

const ShopifyRegister = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [ticketCount, setTicketCount] = useState(200);
  const [price, setPrice] = useState(490.00);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [token, setToken] = useState('');
  const [passwordError, setPasswordError] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    // Extract token from URL query parameters
    const searchParams = new URLSearchParams(location.search);
    const urlToken = searchParams.get('token');
    
    if (!urlToken) {
      setError(t('shopifyRegister.noTokenError'));
      return;
    }
    
    setToken(urlToken);
  }, [location, t]);

  // Fetch price when ticket count changes
  useEffect(() => {
    const fetchPrice = async () => {
      if (ticketCount < 200 || ticketCount > 3000) return;
      
      setLoadingPrice(true);
      try {
        const response = await axios.get(`/api/shopify/shopify/calculate-price`, {
          params: { ticket_count: ticketCount }
        });
        setPrice(response.data.price);
      } catch (err) {
        console.error('Error fetching price:', err);
      } finally {
        setLoadingPrice(false);
      }
    };

    const debounceTimer = setTimeout(fetchPrice, 300);
    return () => clearTimeout(debounceTimer);
  }, [ticketCount]);

  const validateForm = () => {
    if (!username || !password || !confirmPassword) {
      setError(t('shopifyRegister.allFieldsRequired'));
      return false;
    }

    if (password !== confirmPassword) {
      setPasswordError(t('shopifyRegister.passwordMismatch'));
      return false;
    }

    if (password.length < 8) {
      setPasswordError(t('shopifyRegister.passwordTooShort'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setError(null);
    setPasswordError('');
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Create form data
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);
      formData.append('token', token);
      formData.append('ticket_count', ticketCount);
      
      // Submit registration
      const response = await axios.post('/api/shopify/shopify/complete-registration', formData);
      
      if (response.data && response.data.confirmation_url) {
        // Redirect to Shopify payment confirmation page
        window.location.href = response.data.confirmation_url;
      } else {
        setError(t('shopifyRegister.unexpectedResponse'));
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.detail || t('shopifyRegister.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (error && !token) {
    return (
      <div className="una-shopify-register-page">
        <div className="una-shopify-register-container">
          <div className="una-shopify-register-card una-shopify-register-card--error">
            <FaExclamationCircle className="una-register-status-icon" />
            <h2>{t('shopifyRegister.errorTitle')}</h2>
            <p>{error}</p>
            <button
              type="button"
              className="una-register-button"
              onClick={() => (window.location.href = 'https://apps.shopify.com/unanimity-ai')}
            >
              {t('shopifyRegister.returnToShopify')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="una-shopify-register-page">
      <div className="una-shopify-register-container">
        <div className="una-shopify-register-card">
          <div className="una-register-header">
            <img src="/bgless_logo.png" alt="Unanimity AI Logo" className="una-logo" />
            <h2>{t('shopifyRegister.completeRegistration')}</h2>
            <div className="una-shopify-badge">
              <FaShopify />
              <span>{t('shopifyRegister.shopifyConnection')}</span>
            </div>
          </div>

          {error && (
            <div className="una-register-error-message" role="alert">
              <FaExclamationCircle />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="una-register-form">
            <div className="una-form-group">
              <label htmlFor="username">
                <FaUser className="una-input-icon" />
                {t('shopifyRegister.username')}
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t('shopifyRegister.usernamePlaceholder')}
                disabled={loading}
                autoComplete="username"
              />
            </div>

            <div className="una-form-group">
              <label htmlFor="password">
                <FaLock className="una-input-icon" />
                {t('shopifyRegister.password')}
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('shopifyRegister.passwordPlaceholder')}
                disabled={loading}
                className={passwordError ? 'una-input-error' : undefined}
                autoComplete="new-password"
                aria-invalid={Boolean(passwordError)}
                aria-describedby={passwordError ? 'shopify-password-error' : undefined}
              />
            </div>

            <div className="una-form-group">
              <label htmlFor="confirmPassword">
                <FaLock className="una-input-icon" />
                {t('shopifyRegister.confirmPassword')}
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('shopifyRegister.confirmPasswordPlaceholder')}
                disabled={loading}
                className={passwordError ? 'una-input-error' : undefined}
                autoComplete="new-password"
                aria-invalid={Boolean(passwordError)}
                aria-describedby={passwordError ? 'shopify-password-error' : undefined}
              />
              {passwordError && (
                <div className="una-password-error" id="shopify-password-error">
                  <FaExclamationCircle />
                  <span>{passwordError}</span>
                </div>
              )}
            </div>

            <div className="una-password-requirements">
              <p>{t('shopifyRegister.passwordRequirements')}</p>
              <ul>
                <li className={password.length >= 8 ? 'una-requirement-met' : undefined}>
                  {password.length >= 8 ? <FaCheckCircle /> : <FaExclamationCircle />}
                  {t('shopifyRegister.minCharacters')}
                </li>
              </ul>
            </div>

            <div className="una-ticket-selection">
              <label htmlFor="ticketCount">
                <FaTicketAlt className="una-input-icon" />
                {t('shopifyRegister.ticketCount')}
              </label>
              <div className="una-ticket-input-group">
                <input
                  type="range"
                  id="ticketCount"
                  min="200"
                  max="3000"
                  step="100"
                  value={ticketCount}
                  onChange={(e) => setTicketCount(parseInt(e.target.value))}
                  disabled={loading}
                  className="una-ticket-slider"
                />
                <input
                  type="number"
                  min="200"
                  max="3000"
                  value={ticketCount}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (value >= 200 && value <= 3000) {
                      setTicketCount(value);
                    }
                  }}
                  disabled={loading}
                  className="una-ticket-number-input"
                />
              </div>
              <div className="una-pricing-info">
                <div className="una-pricing-tiers">
                  <p className="una-tier-label">{t('shopifyRegister.pricingTiers')}</p>
                  <ul className="una-tier-list">
                    <li>200 {t('shopifyRegister.tickets')}: $490</li>
                    <li>201-1000: $0.90/{t('shopifyRegister.ticket')}</li>
                    <li>1001-2000: $0.80/{t('shopifyRegister.ticket')}</li>
                    <li>2001-3000: $0.70/{t('shopifyRegister.ticket')}</li>
                  </ul>
                </div>
                <div className="una-current-price">
                  <span className="una-price-label">{t('shopifyRegister.monthlyPrice')}:</span>
                  <span className="una-price-value">
                    {loadingPrice ? (
                      <span className="una-loading-spinner-small"></span>
                    ) : (
                      `$${price.toFixed(2)}`
                    )}
                  </span>
                  <span className="una-price-per-ticket">
                    (${(price / ticketCount).toFixed(2)}/{t('shopifyRegister.ticket')})
                  </span>
                </div>
              </div>
            </div>

            <button
              type="submit"
              className="una-register-button"
              disabled={loading}
            >
              {loading ? (
                <div className="una-button-loading">
                  <span className="una-loading-spinner"></span>
                  {t('shopifyRegister.registering')}
                </div>
              ) : (
                t('shopifyRegister.completeRegistration')
              )}
            </button>
          </form>

          <div className="una-register-footer">
            <p>{t('shopifyRegister.termsNotice')}</p>
            <div className="una-help-links">
              <a href="mailto:support@unanimity.ai">{t('shopifyRegister.needHelp')}</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopifyRegister;